import { Confirmations } from "./types";
import empty from "is-empty";
import { cloneDeep } from "lodash";

import { confirmationFormatterByField } from "src/components/views/Confirmations/List/Table/column_defs";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { CONFIRM_EDIT_MODE, FETCH_STATES } from "./constants";
import {
    getDateFromTerm,
    getDefaultTxPurpose,
    getClampedMaturityDate,
    getMaximumPossibleMaturityDate,
    getTermFromDate,
} from "src/utils/index";
import { prepareRowEdit } from "src/utils/validators";

const defaultState = {
    readyToRefresh: false,
    editMode: CONFIRM_EDIT_MODE.OFF_WITH_SAVE,
    tableStatus: [],
    tableChanges: {}, // {"id": originValue: {"quantity": 100}, rowData: {}}
    tableErrors: {}, // {"id": ["quantity", "dividend"]}
    selectedRows: [], // [{rowNode}, {rowNode}...]
    cancelErrors: null, // [id, id, id...]
    newUpdates: {}, //{ids, comment, status}
    selectedConfirmation: null,
    storeRows: [],
    storeSettings: {},
    storeErrors: {},
    confirmations: [],
    confirmationsFilterOptions: {},
};

const getTableStatus = (tableStatus, enabled, keyword) => {
    if (enabled) {
        if (!tableStatus.includes(keyword)) {
            return tableStatus.concat(keyword);
        }
    } else {
        return tableStatus.filter((e) => e !== keyword);
    }
    return tableStatus;
};

export default (state = defaultState, action) => {
    const { id, fieldName, errorType, newValue, oldValue, rowData } =
        action.value || {};
    switch (action.type) {
        case Confirmations.SET_TABLE_STATUS:
            return {
                ...state,
                tableStatus: getTableStatus(
                    state.tableStatus,
                    action.enable,
                    action.keyword
                ),
            };
        case Confirmations.SET_READY_TO_REFRESH:
            return {
                ...state,
                readyToRefresh: action.value,
            };
        case Confirmations.SET_EDIT_MODE:
            return {
                ...state,
                editMode: action.value,
                tableStatus: getTableStatus(
                    state.tableStatus,
                    action.value === CONFIRM_EDIT_MODE.ON,
                    "edit"
                ),
            };
        case Confirmations.RESET_TABLE_ERRORS:
            return { ...state, tableErrors: {} };

        case Confirmations.SET_TABLE_ERRORS:
            let tableErrors = cloneDeep(state.tableErrors);
            const errorExist =
                id in tableErrors && fieldName in tableErrors[id];
            if (errorType) {
                if (!(id in tableErrors)) tableErrors[id] = {};
                tableErrors[id][fieldName] = errorType;
            } else {
                // no error.remove from the list
                if (errorExist) {
                    delete tableErrors[id][fieldName];
                    if (empty(tableErrors[id])) {
                        delete tableErrors[id];
                    }
                }
            }
            return { ...state, tableErrors };

        case Confirmations.RESET_TABLE_CHANGES:
            return { ...state, tableChanges: {} };

        case Confirmations.SET_TABLE_CHANGES:
            let tableChanges = cloneDeep(state.tableChanges);
            const updateExist =
                id in tableChanges &&
                fieldName in tableChanges[id]?.originValue;
            if (updateExist) {
                if (
                    confirmationFormatterByField(
                        fieldName,
                        tableChanges[id].originValue[fieldName]
                    ) === confirmationFormatterByField(fieldName, newValue)
                ) {
                    delete tableChanges[id].originValue[fieldName];
                    if (empty(tableChanges[id].originValue)) {
                        delete tableChanges[id].originValue;
                        delete tableChanges[id].rowData;
                        delete tableChanges[id];
                    } else {
                        tableChanges[id].rowData = rowData;
                    }
                } else {
                    tableChanges[id].rowData = rowData;
                }
            } else if (
                confirmationFormatterByField(fieldName, newValue) !==
                confirmationFormatterByField(fieldName, oldValue)
            ) {
                if (!tableChanges[id]) {
                    tableChanges[id] = { originValue: {} };
                }
                tableChanges[id].originValue[fieldName] = oldValue;
                tableChanges[id].rowData = rowData;
            }
            return { ...state, tableChanges };

        case Confirmations.SET_SELECTED_ROWS:
            return { ...state, selectedRows: action.selectedRows };

        case Confirmations.SET_CANCEL_ERRORS:
            return { ...state, cancelErrors: action.cancelErrors };

        case Confirmations.SET_NEW_UPDATES:
            return { ...state, newUpdates: action.newUpdates };

        case Confirmations.SELECT_CONFIRMATION:
            return {
                ...state,
                selectedConfirmation: action.confirmation
                    ? { ...action.confirmation }
                    : null,
            };

        case Confirmations.PREPARE_STORE:
            return {
                ...state,
                storeRows: action.rowData.map((row, index) => ({
                    ...row,
                    id: index + 1,
                    Callable: false,
                    Dividend: 100,
                    CollateralType: "NC",
                    FR: "F",
                    Maturity: new Date("9999-12-31"),
                    marketData: {
                        status: FETCH_STATES.FETCHING,
                    },
                })),
            };

        case Confirmations.ATTACH_MARKET_DATA:
            const marketData = action.marketData;
            const mdIndex = action.listTicker;

            return {
                ...state,
                storeRows: state.storeRows.map((row) => {
                    const stockId = row.SecurityId;

                    if (!mdIndex.includes(stockId)) return row;

                    const stockData = marketData[stockId]?.data;
                    if (stockData) {
                        const purpose = getDefaultTxPurpose(stockData);

                        const maturity = getMaximumPossibleMaturityDate(
                            stockData.countryCode,
                            purpose,
                            row.ValueDate,
                            stockData.tradableDates,
                            stockData.maxMaturityDates,
                            action.enable90DayMaxMaturity
                        );
                        const TradeableDates = stockData.tradableDates.map(
                            (s) => s.replace(/-/g, "")
                        );
                        return {
                            ...row,
                            marketData: {
                                status: FETCH_STATES.FETCHED,
                                data: stockData,
                                error: null,
                            },
                            hasError: false,
                            Name: stockData.name,
                            LocalName: stockData.localName,
                            Country: stockData.countryCode,
                            BbgCode: stockData.bbgCode,
                            Purpose: purpose,
                            TradeableDates,
                            maxMaturityDates: stockData.maxMaturityDates,
                            Maturity: maturity,
                            Term: getTermFromDate(row.ValueDate, maturity),
                        };
                    }

                    return {
                        ...row,
                        marketData: {
                            status: FETCH_STATES.ERROR,
                            error: "Not Found",
                        },
                        hasError: true,
                        SecurityId: row.SecurityId + " ",
                        Name: "ERROR",
                        LocalName: "ERROR",
                        Country: "ERROR",
                    };
                }),
            };

        case Confirmations.UPDATE_STORE_ROW:
            let newStoreRows = [...state.storeRows];
            newStoreRows.splice(
                action.rowData.id - 1,
                1,
                prepareRowEdit(
                    action.rowData,
                    action.colId,
                    action.featureFlags,
                    true
                )
            );

            return {
                ...state,
                storeRows: newStoreRows,
            };

        case Confirmations.DELETE_STORE_ROW:
            let newStoreRows1 = [...state.storeRows];
            newStoreRows1.splice(action.rowId - 1, 1);

            return {
                ...state,
                storeRows: newStoreRows1.map((row, idx) => ({
                    ...row,
                    id: idx + 1,
                })),
            };

        case Confirmations.APPLY_MATURITY:
            return {
                ...state,
                storeRows: state.storeRows.map((row) => {
                    if (action.maturity.type === "MAX") {
                        const maxMaturity = getMaximumPossibleMaturityDate(
                            row.Country,
                            row.Purpose,
                            row.ValueDate,
                            row.TradeableDates,
                            row.maxMaturityDates,
                            action.enable90DayMaxMaturity
                        );

                        return {
                            ...row,
                            Maturity: maxMaturity,
                            Term: getTermFromDate(row.ValueDate, maxMaturity),
                        };
                    }

                    return {
                        ...row,
                        Maturity: row.ValueDate
                            ? action.maturity.date ||
                              getDateFromTerm(
                                  row.ValueDate,
                                  action.maturity.term
                              )
                            : new Date("9999-12-31"),
                        Term: row.ValueDate
                            ? action.maturity.term ||
                              getTermFromDate(
                                  row.ValueDate,
                                  action.maturity.date
                              )
                            : "",
                    };
                }),
            };

        case Confirmations.SET_STORE_SETTINGS:
            // remove error message on editing
            let storeErrors = cloneDeep(state.storeErrors);
            Object.keys(action.settings).forEach((key) => {
                delete storeErrors[key];
            });

            return {
                ...state,
                storeSettings: {
                    ...state.storeSettings,
                    ...action.settings,
                },
                storeErrors,
            };

        case Confirmations.SET_STORE_ERRORS:
            return {
                ...state,
                storeErrors: {
                    ...state.storeErrors,
                    ...action.errors,
                },
            };
        case Confirmations.SET_CONFIRMATIONS:
            return {
                ...state,
                confirmations: action.responseData,
            };
        case Confirmations.SET_CONFIRMATIONS_FILTER_OPTIONS:
            return {
                ...state,
                confirmationsFilterOptions: action.payload,
            };

        case Confirmations.RESET_STORE:
            return {
                ...state,
                storeRows: [],
                storeSettings: {},
                storeErrors: {},
            };
        default:
            return state;
    }
};
